<template>
  <div class="auth-section">
    <h3 class="section-title">Attendance</h3>

    <div class="section-container">
      <div class="attendance-filter filter-container">
        <span class="filter-label">Date Range</span>

        <select v-model="filterType" class="filter-select" @change="applyFilter(filterType)">
          <option value="last7Days">Last 7 Days</option>
          <option value="thisMonth">This Month</option>
          <option value="cutoff">Current Cutoff</option>
          <option value="previousCutoff">Previous Cutoff</option>
        </select>

        <span class="filter-range-label"> {{ formatDate(dateFrom) }} to {{ formatDate(dateTo) }} </span>
      </div>
      <div class="card-schedule">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>DAP Filing</th>

              <th v-for="n in maxPunches" :key="n">{{ ordinal(n) }} Biotime</th>
              <th v-if="false">Flagged Remarks</th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="loading">
              <td :colspan="showRemarks ? maxPunches + 3 : maxPunches + 2">Loading attendance logs...</td>
            </tr>

            <tr v-else-if="!logs.length">
              <td :colspan="showRemarks ? maxPunches + 3 : maxPunches + 2">No attendance records found.</td>
            </tr>

            <tr v-else v-for="log in logs" :key="log.datelog">
              <td class="date-cell" :class="{ 'weekend-date': isWeekend(log.datelog) }">
                {{ formatDate(log.datelog) }}
              </td>

              <td>
                <span v-if="log.leave" class="leave-badge">
                  {{ log.leave }}
                </span>

                <span v-if="log.ob" class="ob-badge">
                  {{ log.ob }}
                </span>

                <span v-if="log.exception" class="exception-badge">
                  {{ log.exception }}
                </span>

                <span v-if="log.holiday" class="holiday-badge">
                  {{ log.holiday }}
                </span>

                <span v-if="!log.leave && !log.ob && !log.exception && !log.holiday" class="empty"> — </span>
              </td>

              <td v-for="n in maxPunches" :key="n">
                <template v-if="log.punches[n - 1]">
                  <div
                    class="biotime"
                    :class="{
                      'biotime-alert': n === 1 && log.punches[0].io === 'O' && !isProperDayWithLeadingOut(log.punches),
                    }"
                  >
                    <span class="status-dot" :class="log.punches[n - 1].io === 'I' ? 'dot-in' : 'dot-out'"></span>

                    <span :class="log.punches[n - 1].io === 'I' ? 'in' : 'out'">
                      {{ log.punches[n - 1].time }}
                    </span>
                  </div>
                </template>

                <template v-else>
                  <span class="empty">—</span>
                </template>
              </td>

              <td v-if="false" class="remark-cell">
                <span v-if="getAiRemark(log)" class="ai-remark-badge">
                  <span class="ai-remark-icon">✨</span>
                  {{ getAiRemark(log) }}
                </span>

                <span v-else class="empty">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import { computed, inject, onMounted, ref } from 'vue';

export default {
  props: {
    employeeProfileDetails: Object,
  },
  setup(props) {
    const axios = inject('$axios');
    const store = inject('$store');
    const moment = inject('$moment');

    const logs = ref([]);
    const loading = ref(false);
    const maxPunches = ref(0);
    const scheduleEmployee = ref();

    // Whether the employee's schedule includes weekends.
    // Sourced from data.maintenance_schedule_attendance.withWeekend on each fetch.
    // 1 = schedule includes weekends, 0 = it doesn't (Saturday/Sunday are non-working).
    const withWeekend = ref(1);

    // Flagged Remarks column is hidden entirely for "Shifting" schedules —
    // punch-order expectations (Morning IN/OUT, Afternoon IN/OUT) don't apply
    // when the employee's shift moves around, so the remark logic wouldn't
    // be meaningful for them.
    const showRemarks = computed(() => scheduleEmployee.value?.description !== 'Shifting');

    // ---- Date filter state ----
    // 'last7Days' | 'thisMonth' | 'cutoff' | 'previousCutoff'
    const filterType = ref('last7Days');
    const dateFrom = ref('');
    const dateTo = ref('');

    onMounted(() => {
      applyFilter(filterType.value);
    });

    const formatDate = (dateStr) => {
      if (!dateStr) return '--';

      const d = new Date(dateStr);

      return `${d.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      })} - (${d.toLocaleDateString('en-US', {
        weekday: 'short',
      })})`;
    };

    const formatTime = (time) => {
      if (!time) return '--';

      const format = time.length > 5 ? 'HH:mm:ss' : 'HH:mm';

      return moment(time, format).format('hh:mm A');
    };

    const ordinal = (n) => {
      if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;

      switch (n % 10) {
        case 1:
          return `${n}st`;
        case 2:
          return `${n}nd`;
        case 3:
          return `${n}rd`;
        default:
          return `${n}th`;
      }
    };

    // Returns true if the given date string falls on Saturday or Sunday
    const isWeekend = (dateStr) => {
      if (!dateStr) return false;

      const day = moment(dateStr).day(); // 0 = Sunday, 6 = Saturday

      return day === 0 || day === 6;
    };

    // Returns true if the given date string falls specifically on a Saturday
    const isSaturday = (dateStr) => {
      if (!dateStr) return false;

      return moment(dateStr).day() === 6; // 6 = Saturday
    };

    // Returns true when a leave map entry is an AWOP (absent without pay) filing
    const isAwop = (entry) =>
      String(entry?.leaveType || '')
        .toUpperCase()
        .includes('AWOP');

    // ---- Flagged Remarks (incomplete log detection) ----
    //
    // A complete day is expected to have 4 punches in this order:
    //   1. Morning IN   (io: "I")
    //   2. Morning OUT  (io: "O")
    //   3. Afternoon IN (io: "I")
    //   4. Afternoon OUT (io: "O")
    //
    // We walk the actual punches against this expected sequence. Whenever
    // an actual punch's io doesn't match the next expected slot, that slot
    // is flagged as missing and we advance the expected pointer until it
    // realigns. This correctly identifies gaps anywhere in the sequence
    // (not just the end) — e.g. 3 logs of I, O, I is missing "Afternoon OUT".
    const expectedPunchLabels = ['Morning IN', 'Morning OUT', 'Afternoon IN', 'Afternoon OUT'];
    const expectedPunchIO = ['I', 'O', 'I', 'O'];

    const getMissingPunches = (punches) => {
      let ei = 0;
      const missing = [];

      punches.forEach((p) => {
        while (ei < expectedPunchIO.length && expectedPunchIO[ei] !== p.io) {
          missing.push(expectedPunchLabels[ei]);
          ei++;
        }
        // matched (or ran out of expected slots) — move past this slot
        if (ei < expectedPunchIO.length) ei++;
      });

      while (ei < expectedPunchIO.length) {
        missing.push(expectedPunchLabels[ei]);
        ei++;
      }

      return missing;
    };

    // True when a day starts with a stray OUT (carry-over from the
    // previous day) but everything after it is still a complete, proper
    // day — Morning IN/OUT + Afternoon IN/OUT. In this case the leading
    // OUT isn't actually a problem, so no alert/remark should be shown.
    const isProperDayWithLeadingOut = (punches) => {
      if (punches.length !== 5 || punches[0].io !== 'O') return false;

      const restIO = punches.slice(1).map((p) => p.io);

      return restIO.every((io, i) => io === expectedPunchIO[i]);
    };

    // Returns a human-readable remark string when a working day's logs are
    // incomplete or irregular, or null when there's nothing to flag (day is
    // fully explained by a Leave/OB/Exception/Holiday filing, is an
    // exempted non-working Saturday, or logs are complete and normal).
    // Note: for "Shifting" schedules the entire Flagged Remarks column is
    // hidden via `showRemarks`, so this function's output isn't rendered
    // at all in that case — no separate check is needed here.
    const getAiRemark = (log) => {
      // Already explained by a filing — no need to flag
      if (log.leave || log.ob || log.exception || log.holiday) return null;

      // Saturday is exempted from AI remarks when the employee's schedule
      // does not include weekends — it's not a required working day, so
      // incomplete/missing punches shouldn't be flagged for correction.
      if (withWeekend.value == 0 && isSaturday(log.datelog)) return null;

      const punches = log.punches;
      const count = punches.length;
      const remarks = [];

      if (count === 0) {
        return 'No time logs recorded ';
      }

      // Special case: first log is a stray OUT (carry-over from the
      // previous day), but everything after it is still a complete,
      // proper day — Morning IN/OUT + Afternoon IN/OUT. This is fine,
      // no correction needed.
      if (isProperDayWithLeadingOut(punches)) return null;

      // First punch of the day is an OUT — the employee likely forgot to
      // log out the previous day, so this OUT is a carry-over.
      if (punches[0].io === 'O') {
        remarks.push('First log is OUT — likely forgot to log out the previous day. Subject for Attendance Correction (AC).');
      }

      if (count === 5) {
        // An extra 5th punch usually means a duplicate or stray tap —
        // flag it directly rather than trying to guess which slot is off.
        remarks.push('5 logs detected — please correct the logs for AC.');
      } else if (count < 4) {
        const missing = getMissingPunches(punches);

        if (missing.length) {
          remarks.push(`Missing ${missing.join(', ')} `);
        }
      }

      return remarks.length ? remarks.join(' ') : null;
    };

    // ---- Date range calculators ----

    // "Last 7 Days" -> 6 days ago through today (7 days total, inclusive)
    const getLast7DaysRange = () => {
      const today = moment();
      const sevenDaysAgo = moment().subtract(6, 'days');

      return {
        from: sevenDaysAgo.format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      };
    };

    // "This Month" -> 1st of current month through today
    const getThisMonthRange = () => {
      const today = moment();
      const startOfMonth = moment().startOf('month');

      return {
        from: startOfMonth.format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      };
    };

    // "Current Cutoff"
    const getCutoffRange = () => {
      const today = moment();
      const day = today.date();

      let from;
      let to;

      if (day >= 1 && day <= 15) {
        from = moment().subtract(1, 'month').date(22);
        to = moment().date(6);
      } else {
        from = moment().date(7);
        to = moment().date(21);
      }

      return {
        from: from.format('YYYY-MM-DD'),
        to: to.format('YYYY-MM-DD'),
      };
    };

    // "Previous Cutoff" -> the cutoff period immediately before the current one.
    const getPreviousCutoffRange = () => {
      const today = moment();
      const day = today.date();

      let from;
      let to;

      if (day >= 1 && day <= 15) {
        from = moment().subtract(1, 'month').date(7);
        to = moment().subtract(1, 'month').date(21);
      } else {
        from = moment().subtract(1, 'month').date(22);
        to = moment().date(6);
      }

      return {
        from: from.format('YYYY-MM-DD'),
        to: to.format('YYYY-MM-DD'),
      };
    };

    const applyFilter = (type) => {
      filterType.value = type;

      let range;

      if (type === 'last7Days') {
        range = getLast7DaysRange();
      } else if (type === 'thisMonth') {
        range = getThisMonthRange();
      } else if (type === 'cutoff') {
        range = getCutoffRange();
      } else {
        // previousCutoff
        range = getPreviousCutoffRange();
      }

      dateFrom.value = range.from;
      dateTo.value = range.to;

      getAttendanceLogs();
    };

    const getAttendanceLogs = async () => {
      loading.value = true;

      try {
        const userRole = localStorage.getItem(`${process.env.VUE_APP_USER_ROLE}_XXX`);

        let data;

        const params = {
          dateFrom: dateFrom.value,
          dateTo: dateTo.value,
        };

        if (userRole === 'user') {
          ({ data } = await axios.get('/api/integration/getEmployees/id', {
            params,
          }));
        } else if (userRole === 'admin') {
          ({ data } = await axios.get(`/api/integration/getEmployees/dtr/${props.employeeProfileDetails.id}`, { params }));
        }

        const noWeekendSched = data?.maintenance_schedule_attendance?.withWeekend ?? 1;

        withWeekend.value = noWeekendSched;

        const rawLogs = data?.dtrs ?? [];
        const leaveLogs = data?.leaveForms ?? [];
        const OBLogs = data?.obForms ?? [];
        const exceptionAttendanceLog = data?.exceptions ?? [];
        const holidayLog = data?.holidays ?? [];
        const scheduleLog = data?.maintenance_schedule_attendance ?? [];
        scheduleEmployee.value = scheduleLog;
        // Sort attendance logs
        rawLogs.sort((a, b) => {
          if (a.datelog === b.datelog) {
            return a.timelog.localeCompare(b.timelog);
          }
          return a.datelog.localeCompare(b.datelog);
        });

        // ---- Build leave map (supports per-day categories) ----
        //
        // Preferred shape (new): each leave filing can optionally carry a
        // `days` array so a single filing can mix Whole Day / Half Day
        // across its date range, e.g.:
        //   leave.days = [
        //     { date: "2026-07-23", leaveCategory: "Whole Day" },
        //     { date: "2026-07-24", leaveCategory: "Half Day (AM)" },
        //   ]
        //
        // Backward-compatible fallback (old): if `days` is not present,
        // the single `leave.leaveCategory` is stamped across every date
        // in [dateStart, dateEnd], same as before.
        const leaveMap = {};

        leaveLogs.forEach((leave) => {
          if (Array.isArray(leave.days) && leave.days.length > 0) {
            leave.days.forEach((day) => {
              const date = moment(day.date).format('YYYY-MM-DD');

              leaveMap[date] = {
                leaveType: leave.leaveType,
                leaveCategory: day.leaveCategory,
                label: `${leave.leaveType} (${day.leaveCategory})`,
              };
            });

            return;
          }

          // Fallback: single category applied across the whole range
          let current = moment(leave.dateStart);
          const end = moment(leave.dateEnd);

          while (current.isSameOrBefore(end, 'day')) {
            leaveMap[current.format('YYYY-MM-DD')] = {
              leaveType: leave.leaveType,
              leaveCategory: leave.leaveCategory,
              label: `${leave.leaveType} (${leave.leaveCategory})`,
            };

            current.add(1, 'day');
          }
        });

        // Build OB (official business) map
        const obMap = {};

        OBLogs.forEach((ob) => {
          const date = moment(ob.obDate).format('YYYY-MM-DD');

          obMap[date] = {
            obTime: formatTime(ob.obTime),
            returnTime: formatTime(ob.returnTime),
            label: `Official Business`,
          };
        });

        // Build attendance exceptions map (date range: date_from -> date_to)
        const exceptionMap = {};

        exceptionAttendanceLog.forEach((exception) => {
          let current = moment(exception.date_from);
          const end = moment(exception.date_to);

          while (current.isSameOrBefore(end, 'day')) {
            exceptionMap[current.format('YYYY-MM-DD')] = {
              exceptionType: exception.exception_type,
              exceptionCategory: exception.exception_category,
              exceptionScope: exception.exception_scope,
              remarks: exception.exception_remarks,
              label: exception.exception_type,
            };

            current.add(1, 'day');
          }
        });

        // Build holiday map (single day: dbDate)
        const holidayMap = {};

        holidayLog.forEach((holiday) => {
          const date = moment(holiday.dbDate).format('YYYY-MM-DD');

          holidayMap[date] = {
            description: holiday.description,
            label: holiday.description,
          };
        });

        // Create a set of dates that have DTR
        const dtrDates = new Set(rawLogs.map((log) => log.datelog));

        const grouped = {};

        // Generate dates
        let current = moment(dateFrom.value);
        const end = moment(dateTo.value);

        while (current.isSameOrBefore(end, 'day')) {
          const date = current.format('YYYY-MM-DD');
          const dayOfWeek = current.day();
          const isWeekendDay = dayOfWeek === 0 || dayOfWeek === 6;
          const isSunday = dayOfWeek === 0;

          // Always skip Sunday if there's no DTR for that date
          if (isSunday && !dtrDates.has(date)) {
            current.add(1, 'day');
            continue;
          }

          // An AWOP filing on a non-working weekend shouldn't keep the row visible
          const hasRealLeave = !!leaveMap[date] && !isAwop(leaveMap[date]);

          // Skip weekends if schedule doesn't include weekends,
          // unless there is a DTR, a non-AWOP Leave, OB, Exception, or Holiday on that date.
          if (noWeekendSched == 0 && isWeekendDay && !dtrDates.has(date) && !hasRealLeave && !obMap[date] && !exceptionMap[date] && !holidayMap[date]) {
            current.add(1, 'day');
            continue;
          }

          grouped[date] = {
            datelog: date,
            punches: [],
            leave: leaveMap[date]?.label || null,
            ob: obMap[date]?.label || null,
            exception: exceptionMap[date]?.label || null,
            holiday: holidayMap[date]?.label || null,
          };

          current.add(1, 'day');
        }

        // Populate attendance logs
        rawLogs.forEach((log) => {
          if (!grouped[log.datelog]) {
            grouped[log.datelog] = {
              datelog: log.datelog,
              punches: [],
              leave: leaveMap[log.datelog]?.label || null,
              ob: obMap[log.datelog]?.label || null,
              exception: exceptionMap[log.datelog]?.label || null,
              holiday: holidayMap[log.datelog]?.label || null,
            };
          }

          grouped[log.datelog].punches.push({
            time: formatTime(log.timelog),
            io: log.io,
          });
        });

        // Sort logs
        logs.value = Object.values(grouped).sort((a, b) => a.datelog.localeCompare(b.datelog));

        // Determine maximum punches
        maxPunches.value = logs.value.reduce((max, row) => {
          return Math.max(max, row.punches.length);
        }, 0);
      } catch (error) {
        store.commit('errorHandler', error);
      } finally {
        loading.value = false;
      }
    };

    return {
      logs,
      loading,
      maxPunches,
      filterType,
      dateFrom,
      dateTo,
      withWeekend,
      showRemarks,
      formatDate,
      formatTime,
      ordinal,
      isWeekend,
      isSaturday,
      isProperDayWithLeadingOut,
      getAiRemark,
      getAttendanceLogs,
      applyFilter,
      scheduleEmployee,
    };
  },
};
</script>
<style scoped>
.auth-section {
  padding-top: 40px;
}

.section-title {
  margin: 0 0 12px 0;
  padding-bottom: 8px;
}

.section-container {
  padding: 12px 0px;
}
table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  font-family: 'Montserrat';
}

th,
td {
  border: 1px solid #edf2f7;
  padding: 12px 16px;
  text-align: center;
  vertical-align: middle;
}

th {
  background: #f8fafc;
  color: #334155;
  font-weight: 700;
  white-space: nowrap;
  position: sticky;
  top: 0;
}

tbody tr:nth-child(even) {
  background: #fcfcfd;
}

tbody tr:hover {
  background: #f8fbff;
  transition: 0.2s ease;
}

.date-cell {
  color: #6c6d6e;
  font-weight: 600;
  white-space: nowrap;
  text-align: left;
}

.weekend-date {
  color: #dc2626;
  font-weight: 700;
}

.remark-cell {
  text-align: left;
  white-space: normal;
  min-width: 220px;
}

.ai-remark-badge {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  background: linear-gradient(135deg, #fff7ed, #fef2f2);
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  text-align: left;
}

.ai-remark-icon {
  flex-shrink: 0;
  font-size: 12px;
}

.biotime {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
}

.biotime-alert {
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 6px;
  padding: 3px 8px;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dot-in {
  background: #22c55e;
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.35);
}

.dot-out {
  background: #ef4444;
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.35);
}

.in {
  color: #15803d;
  font-weight: 600;
}

.out {
  color: #b91c1c;
  font-weight: 600;
}

.leave-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 10px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.empty {
  color: #cbd5e1;
}

.auth-section {
  width: 100%;
}

.section-container {
  margin-top: 15px;
}

.card-schedule {
  padding: 20px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow-x: auto;
}

h3 {
  font-family: 'Montserrat';
  font-weight: 800;
  color: #3e535f;
  margin-bottom: 15px;
}

.description {
  font-weight: 500;
  color: #64748b;
}

.flex {
  display: flex;
  margin: 10px 0;
}

.card-rows {
  margin: 20px 0;
}

.card-rows:hover {
  background-color: #fafafa;
}

.card-cols {
  display: flex;
  gap: 20px;
  margin: 20px 0;
}

.card-day-sched {
  display: flex;
  width: 55px;
  height: 55px;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  border: 1px solid #3e535f;
  color: #3e535f;
  font-size: 14px;
  font-weight: 600;
  flex-direction: column;
}

.card-time {
  width: 55px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #3e535f;
  font-size: 14px;
  font-weight: 500;
}

.duty {
  background: #3e535f;
  color: white;
}

.tooltiptext {
  min-width: 150px;
  background: #666;
  color: white;
  text-align: center;
  padding: 6px;
  border-radius: 6px;
  font-family: 'Montserrat';
  font-size: 12px;
}

.header-sched {
  display: flex;
  justify-content: space-between;
}

.action-toggle {
  cursor: pointer;
}

.leave-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.85em;
  background-color: #e0f2fe; /* light blue */
  color: #0369a1;
}

.ob-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.85em;
  background-color: #f3e8ff; /* light purple */
  color: #7e22ce;
}

.exception-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.85em;
  background-color: #fee2e2; /* light red */
  color: #b91c1c;
}

.holiday-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.85em;
  background-color: #ffedd5; /* light orange */
  color: #c2410c;
}
::-webkit-scrollbar {
  height: 8px;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 20px;
}

::-webkit-scrollbar-track {
  background: #f8fafc;
}

/* Mobile */

@media only screen and (max-width: 600px) {
  .auth-section {
    padding-top: 30px;
  }

  .section-container {
    margin: 10px;
  }

  table {
    font-size: 11px;
  }

  th,
  td {
    padding: 8px;
  }

  .card-schedule {
    padding: 10px;
  }

  h3 {
    font-size: 15px;
  }

  .status-dot {
    width: 8px;
    height: 8px;
  }

  .biotime {
    gap: 4px;
  }

  .biotime-alert {
    padding: 2px 5px;
  }

  .leave-badge {
    font-size: 10px;
    padding: 3px 7px;
  }

  .remark-cell {
    min-width: 140px;
  }

  .ai-remark-badge {
    font-size: 10px;
    padding: 4px 7px;
  }

  .card-cols {
    gap: 5px;
    margin: 10px 0;
  }

  .card-day-sched {
    width: 30px;
    height: 30px;
    font-size: 8px;
  }

  .card-time {
    width: 30px;
    font-size: 10px;
  }
}

.filter-container {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 16px;
  flex-wrap: wrap;
}

.filter-label {
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
}

.filter-select {
  min-width: 180px;
  padding: 10px 38px 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: #fff;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  outline: none;

  /* Custom arrow */
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
}

.filter-select:hover {
  border-color: #9ca3af;
}

.filter-select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.filter-range-label {
  font-size: 13px;
  font-weight: 600;
  color: #3e535f;
  background: #f1f5f9;
  padding: 6px 12px;
  border-radius: 999px;
  white-space: nowrap;
}

@media only screen and (max-width: 600px) {
  .filter-container {
    align-items: stretch;
  }

  .filter-select {
    min-width: 0;
    flex: 1;
  }
}
</style>
